import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../../../../../store/store';
import { fetchUserSession, clearSession } from '../../../../../../store/AuthSlice';
import { persistor } from '../../../../../../store/store';
import { clearCart, getFromLocalStorage, setWishlist } from '../../../../../../store/UserSlice';
import { clearPersonalDiscounts, fetchProducts } from '../../../../../../store/ProductSlice';
import type { Order } from '../../../../../../types/common';
import { getCsrfToken } from '../../../../../../utils/helpers';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '../../../../../../constants/routes';
import type { AccountSectionKey } from './account.helpers';
import AccountNav from './AccountNav';
import FavoritesPanel from './FavoritesPanel';
import MyDataPanel from './MyDataPanel';
import OrdersPanel from './OrdersPanel';
import ReviewsPanel from './ReviewsPanel';
import DiscountsPanel from './DiscountsPanel';
import BonusesPanel from './BonusesPanel';
import styles from './UserCardNew.module.scss';

type Props = {
    setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
    isMobile: boolean;
    mobileMenuOpen: boolean;
};

const ACCOUNT_SECTION_BY_PARAM: Record<string, AccountSectionKey> = {
    wishlist: 'wishlist',
    orders: 'orders',
    reviews: 'reviews',
    discounts: 'discounts',
    bonuses: 'bonuses',
};
const ACCOUNT_SECTION_PATHS: Record<AccountSectionKey, string> = {
    wishlist: PATHS.wishlist,
    myData: PATHS.account,
    orders: `${PATHS.account}/orders`,
    reviews: `${PATHS.account}/reviews`,
    discounts: `${PATHS.account}/discounts`,
    bonuses: `${PATHS.account}/bonuses`,
};

const UserCardNew: FC<Props> = ({ setIsLoggedIn, isMobile, mobileMenuOpen }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { section } = useParams<{ section?: string }>();
    const { user, loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);
    const active = useMemo<AccountSectionKey>(() => {
        if (!section) return 'myData';
        return ACCOUNT_SECTION_BY_PARAM[section] ?? 'myData';
    }, [section]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const ordersAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        dispatch(fetchUserSession());
    }, [dispatch]);

    useEffect(() => {
        if (!section) return;
        if (ACCOUNT_SECTION_BY_PARAM[section]) return;
        navigate(PATHS.account, { replace: true });
    }, [section, navigate]);

    useEffect(() => {
        if (!isAuthenticated && !user && !loading) dispatch(fetchUserSession());
        if (isAuthenticated) {
            dispatch(getFromLocalStorage('wishlist'));
            dispatch(getFromLocalStorage('cart'));
            dispatch(fetchProducts());
        }
    }, [isAuthenticated, user, loading, dispatch]);

    useEffect(() => {
        if (!user?._id) return;
        ordersAbortRef.current?.abort();
        const ctrl = new AbortController();
        ordersAbortRef.current = ctrl;
        setOrdersLoading(true);
        setOrdersError(null);
        fetch(`${process.env.REACT_APP_SERVER_URL}/orders/my`, {
            credentials: 'include',
            signal: ctrl.signal,
        })
            .then((res) => {
                if (!res.ok) throw new Error('Ошибка при получении заказов');
                return res.json();
            })
            .then((data: Order[]) => setOrders(Array.isArray(data) ? data : []))
            .catch((err) => {
                if (err?.name === 'AbortError') return;
                setOrdersError(err?.message || 'Ошибка загрузки заказов');
            })
            .finally(() => setOrdersLoading(false));
        return () => ctrl.abort();
    }, [user?._id]);

    const handleSelect = useCallback(
        (key: AccountSectionKey) => {
            navigate(ACCOUNT_SECTION_PATHS[key]);
        },
        [navigate],
    );

    const handleLogout = async () => {
        try {
            const csrfToken = await getCsrfToken();
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'csrf-token': csrfToken },
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Ошибка при логауте:', errorData?.message || response.statusText);
                return;
            }
        } catch (e) {
            console.error('Ошибка при логауте:', e);
            return;
        }
        dispatch(clearSession());
        await persistor.purge();
        localStorage.removeItem('wishlist');
        localStorage.removeItem('cart');
        dispatch(clearPersonalDiscounts());
        dispatch(setWishlist([]));
        dispatch(clearCart());
        dispatch(fetchProducts());
        setIsLoggedIn(false);
    };

    const content = useMemo(() => {
        if (!user) return null;
        switch (active) {
            case 'wishlist':
                return <FavoritesPanel />;
            case 'myData':
                return <MyDataPanel user={user} />;
            case 'orders':
                return <OrdersPanel orders={orders} loading={ordersLoading} error={ordersError} />;
            case 'reviews':
                return <ReviewsPanel />;
            case 'discounts':
                return <DiscountsPanel />;
            case 'bonuses':
                return <BonusesPanel user={user} />;
            default:
                return <MyDataPanel user={user} />;
        }
    }, [active, user, orders, ordersLoading, ordersError]);

    if (!user) return <div className={styles.state}>Загрузка данных пользователя…</div>;
    if (loading) return <div className={styles.state}>Загрузка данных пользователя…</div>;
    if (error) return <div className={styles.stateError}>Ошибка: {String(error)}</div>;

    return (
        <div className={styles.layout}>
            <aside
                className={styles.navCol + (isMobile && !mobileMenuOpen ? ` ${styles.navColMobileHidden}` : '')}
                aria-label="Навигация по разделам личного кабинета"
            >
                <AccountNav active={active} onSelect={handleSelect} onLogout={handleLogout} disabled={false} isMobile={isMobile} mobileMenuOpen={mobileMenuOpen} />
            </aside>
            <main className={styles.contentCol} aria-label="Контент личного кабинета">
                {content}
            </main>
        </div>
    );
};

export default UserCardNew;
